import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Hotel, ArrowLeft, Send, Sparkles, MapPin, Mail, Phone, 
  BedDouble, Users, DollarSign, CheckCircle, Building, Globe
} from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/lib/backendUrl';

function ChatBubble({ role, content }) {
  return (
    <div className={`flex ${role === 'user' ? 'justify-end' : 'justify-start'} message-enter`}>
      {role === 'assistant' && (
        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center mr-2 flex-shrink-0">
          <Sparkles className="w-4 h-4 text-primary" />
        </div>
      )}
      <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${
        role === 'user' 
          ? 'bg-primary text-primary-foreground' 
          : 'bg-card border border-border shadow-sm'
      }`}>
        <p className="text-sm leading-relaxed whitespace-pre-wrap">{content}</p>
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex items-center gap-2 message-enter">
      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
        <Sparkles className="w-4 h-4 text-primary" />
      </div>
      <div className="bg-card border border-border rounded-2xl px-4 py-3">
        <div className="flex gap-1">
          <div className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '0ms' }} />
          <div className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '150ms' }} />
          <div className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    </div>
  );
}

function HotelPreviewPanel({ hotelData, rooms, status, onImageUpload }) {
  const hasBasicInfo = hotelData.name && hotelData.city && hotelData.country;
  const fileInputRef = useRef(null);
  
  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        onImageUpload(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-6">
      {/* Preview Header */}
      <div>
        <h2 className="text-xl font-bold heading-font mb-1">Live Preview</h2>
        <p className="text-sm text-muted-foreground">See your hotel listing as you build it</p>
      </div>

      {/* Hotel Card Preview */}
      {hasBasicInfo ? (
        <Card className="border-2 border-primary/20 shadow-lg" data-testid="hotel-preview-card">
          <CardHeader className="relative bg-gradient-to-br from-primary/5 to-primary/10 pb-20">
            {/* Hotel Image */}
            {hotelData.image_url ? (
              <div className="absolute inset-0 -z-0">
                <img 
                  src={hotelData.image_url} 
                  alt={hotelData.name}
                  className="w-full h-full object-cover opacity-40"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
              </div>
            ) : (
              <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(45,212,191,0.3),transparent_70%)]" />
              </div>
            )}
            <div className="relative">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-xl bg-primary/20 backdrop-blur-sm flex items-center justify-center">
                    <Hotel className="w-5 h-5 text-primary" />
                  </div>
                  <Badge variant="secondary" className="text-xs">New Listing</Badge>
                </div>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="text-xs backdrop-blur-sm bg-background/80"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="upload-hotel-image-button"
                >
                  <Building className="w-3 h-3 mr-1" />
                  {hotelData.image_url ? 'Change' : 'Add'} Image
                </Button>
                <input 
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                />
              </div>
              <CardTitle className="text-2xl heading-font mb-2">{hotelData.name}</CardTitle>
              {(hotelData.city || hotelData.country) && (
                <CardDescription className="flex items-center gap-1 text-base">
                  <MapPin className="w-4 h-4" />
                  {hotelData.city}{hotelData.city && hotelData.country && ', '}{hotelData.country}
                </CardDescription>
              )}
            </div>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            {/* Description */}
            {hotelData.description && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Description</p>
                <p className="text-sm leading-relaxed">{hotelData.description}</p>
              </div>
            )}

            {/* Details Grid */}
            <div className="grid grid-cols-2 gap-3 pt-3 border-t border-border">
              {hotelData.category && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Type</p>
                  <Badge variant="outline">{hotelData.category}</Badge>
                </div>
              )}
              {hotelData.email && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Contact</p>
                  <div className="flex items-center gap-1 text-xs">
                    <Mail className="w-3 h-3" />
                    <span className="truncate">{hotelData.email}</span>
                  </div>
                </div>
              )}
              {hotelData.phone && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Phone</p>
                  <div className="flex items-center gap-1 text-xs">
                    <Phone className="w-3 h-3" />
                    <span>{hotelData.phone}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Rooms Section */}
            {rooms.length > 0 && (
              <div className="pt-4 border-t border-border">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold">Rooms Added</p>
                  <Badge variant="secondary">{rooms.length}</Badge>
                </div>
                <div className="space-y-2">
                  {rooms.map((room, idx) => (
                    <div key={idx} className="bg-muted/50 rounded-lg p-3 border border-border">
                      <div className="flex justify-between items-start mb-1">
                        <h4 className="font-semibold text-sm">{room.room_type}</h4>
                        <span className="font-bold mono text-sm">${room.price_per_night}/night</span>
                      </div>
                      {room.description && (
                        <p className="text-xs text-muted-foreground mb-2">{room.description}</p>
                      )}
                      <div className="flex gap-2">
                        <Badge variant="outline" className="text-[10px]">
                          <Users className="w-2.5 h-2.5 mr-1" />
                          {room.capacity} guests
                        </Badge>
                        {room.beds && (
                          <Badge variant="outline" className="text-[10px]">
                            <BedDouble className="w-2.5 h-2.5 mr-1" />
                            {room.beds} bed{room.beds > 1 ? 's' : ''}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Status Indicator */}
            {status === 'ready_to_publish' && (
              <Alert className="bg-emerald-50 border-emerald-200">
                <CheckCircle className="w-4 h-4 text-emerald-600" />
                <AlertDescription className="text-sm">
                  Your listing looks great! Review the details above and publish when ready.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="border-dashed border-2 border-muted-foreground/20" data-testid="hotel-preview-empty">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 rounded-2xl bg-muted/50 flex items-center justify-center mx-auto mb-4">
              <Building className="w-8 h-8 text-muted-foreground/40" />
            </div>
            <h3 className="text-lg font-semibold heading-font mb-2">Your Hotel Preview</h3>
            <p className="text-sm text-muted-foreground max-w-sm mx-auto">
              Start chatting with our AI assistant, and watch your hotel listing come to life here in real-time!
            </p>
          </CardContent>
        </Card>
      )}

      {/* Features */}
      <div className="bg-gradient-to-br from-primary/5 to-transparent rounded-xl p-4 border border-primary/10">
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" />
          What You'll Get
        </h3>
        <ul className="space-y-2 text-xs text-muted-foreground">
          <li className="flex items-center gap-2">
            <CheckCircle className="w-3 h-3 text-primary flex-shrink-0" />
            Instant visibility to crypto-paying guests
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle className="w-3 h-3 text-primary flex-shrink-0" />
            AI-powered booking assistant for your hotel
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle className="w-3 h-3 text-primary flex-shrink-0" />
            Multi-chain crypto payment support
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle className="w-3 h-3 text-primary flex-shrink-0" />
            Pay-as-you-use tiered revenue model (3-7%)
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle className="w-3 h-3 text-primary flex-shrink-0" />
            NFT booking receipts with resale royalties
          </li>
        </ul>
      </div>
    </div>
  );
}

export default function ListHotelPage() {
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  
  const [sessionId] = useState(() => `list_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [hotelData, setHotelData] = useState({});
  const [rooms, setRooms] = useState([]);
  const [status, setStatus] = useState('collecting');
  const [publishing, setPublishing] = useState(false);

  // Initial welcome message
  useEffect(() => {
    setMessages([{
      role: 'assistant',
      content: "Hello! 👋 I'm excited to help you list your hotel on getbitsy.ai.\n\nLet's start simple: What's the name of your hotel?"
    }]);
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const handleImageUpload = (imageDataUrl) => {
    setHotelData(prev => ({ ...prev, image_url: imageDataUrl }));
    toast.success('Hotel image added to preview!');
  };

  const sendMessage = async (text) => {
    if (!text.trim()) return;
    
    const userMessage = text.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const backendUrl = BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/chat/list-hotel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          message: userMessage
        })
      });

      const data = await response.json();
      
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: data.message 
      }]);
      
      setHotelData(data.hotel_data || {});
      setRooms(data.rooms || []);
      setStatus(data.status || 'collecting');
      
    } catch (error) {
      console.error('Chat error:', error);
      toast.error('Failed to send message');
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "Sorry, I'm having trouble right now. Please try again." 
      }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const backendUrl = BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/chat/list-hotel/publish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          owner_id: 'demo_owner'  // TODO: Get from auth context
        })
      });

      const result = await response.json();
      
      if (result.success) {
        toast.success(result.message || 'Hotel published successfully!');
        
        // Show success message in chat
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: `🎉 ${result.message}\n\nYou can now manage your hotel from your dashboard. Redirecting you there...` 
        }]);
        
        // Redirect to dashboard after 3 seconds
        setTimeout(() => {
          navigate('/dashboard');
        }, 3000);
      } else {
        toast.error('Failed to publish hotel');
      }
    } catch (error) {
      console.error('Publish error:', error);
      toast.error('Failed to publish hotel listing');
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="lobby-theme h-screen flex flex-col">
      {/* Header */}
      <header className="border-b border-border bg-card/60 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate('/')}
              data-testid="list-hotel-back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-bold heading-font text-lg">List Your Hotel</h1>
              <p className="text-xs text-muted-foreground">Chat with AI to get listed</p>
            </div>
          </div>
          <Badge variant="secondary" className="text-xs">
            <Sparkles className="w-3 h-3 mr-1" />
            AI-Powered
          </Badge>
        </div>
      </header>

      {/* Split Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left: Chat */}
        <div className="flex-1 lg:flex-[0.5] flex flex-col border-r border-border">
          <div ref={scrollRef} className="flex-1 overflow-y-auto chat-scroll px-4 py-6">
            <div className="max-w-2xl mx-auto space-y-4">
              {/* Welcome */}
              {messages.length === 0 && (
                <div className="text-center py-12 message-enter">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <Sparkles className="w-8 h-8 text-primary" />
                  </div>
                  <h2 className="text-2xl font-bold heading-font mb-2">Welcome, Hotel Owner!</h2>
                  <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                    Let's get your property listed on getbitsy.ai. I'll guide you through the process - just answer my questions naturally.
                  </p>
                </div>
              )}

              {/* Messages */}
              {messages.map((msg, i) => (
                <ChatBubble key={i} role={msg.role} content={msg.content} />
              ))}

              {loading && <TypingIndicator />}
            </div>
          </div>

          {/* Input */}
          <div className="sticky bottom-0 z-20 bg-background/90 backdrop-blur-md border-t border-border">
            <div className="max-w-2xl mx-auto px-4 py-4">
              {status === 'ready_to_publish' && (
                <div className="mb-3">
                  <Button 
                    onClick={handlePublish}
                    disabled={publishing}
                    className="w-full h-11 rounded-xl text-base font-semibold"
                    data-testid="publish-hotel-button"
                  >
                    {publishing ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Publishing...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-5 h-5 mr-2" />
                        Publish Hotel Listing
                      </>
                    )}
                  </Button>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Input 
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage(input)}
                  placeholder="Type your answer..."
                  className="h-12 rounded-2xl text-base"
                  data-testid="list-hotel-chat-input"
                  disabled={status === 'ready_to_publish' && publishing}
                />
                <Button 
                  size="icon" 
                  className="h-12 w-12 rounded-xl flex-shrink-0"
                  onClick={() => sendMessage(input)}
                  disabled={!input.trim() || loading || publishing}
                  data-testid="list-hotel-send-button"
                >
                  <Send className="w-5 h-5" />
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground text-center mt-2">
                Answer naturally - the AI will extract the details
              </p>
            </div>
          </div>
        </div>

        {/* Right: Live Preview */}
        <aside className="hidden lg:flex lg:w-[45%] xl:w-[50%] border-l border-border bg-gradient-to-br from-card/80 to-card/40 flex-col overflow-hidden">
          <HotelPreviewPanel hotelData={hotelData} rooms={rooms} status={status} onImageUpload={handleImageUpload} />
        </aside>
      </div>
    </div>
  );
}
